import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listUsuarios,
  createUsuario,
  updateUsuario,
  removeUsuario,
} from "../api";
import type { Usuario } from "../types";
import { SYSTEMS } from "../integration";
import { ABAS_POR_SISTEMA } from "../abas-sistemas";
import { compararNomes, ordenarPorNome } from "../ordem";

/**
 * Checkboxes de "Sistemas liberados" em ordem alfabética (cópia — o SYSTEMS
 * original segue na ordem dos cards da Área de Trabalho, que é proposital).
 */
const SISTEMAS_EM_ORDEM = ordenarPorNome(SYSTEMS, (s) => s.nome);

type FormState = {
  nome: string;
  usuario: string;
  email: string;
  cargo: string;
  senha: string;
  ativo: "Ativo" | "Inativo";
  admin: boolean;
  sistemas: string[];
  /** Sistemas em que a pessoa só VISUALIZA; nos demais liberados, acesso normal. */
  sistemasLeitura: string[];
  /** Abas de cada sistema só-visualização. Sistema ausente = todas. */
  abas: Record<string, string[]>;
};

const EMPTY: FormState = {
  nome: "",
  usuario: "",
  email: "",
  cargo: "",
  senha: "",
  ativo: "Ativo",
  admin: false,
  sistemas: [],
  sistemasLeitura: [],
  abas: {},
};

/** Painel de gestão de usuários do Portal: criar, editar, excluir — com cargo. */
export default function Usuarios({ onBack }: { onBack: () => void }) {
  const [rows, setRows] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Usuario | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  /**
   * O que aconteceu com o LOGIN nos outros sistemas ao salvar (login único).
   * Sem isso o admin não tinha como saber que o acesso à Gestão da Operação e
   * ao Relatórios já foi criado — ou que um deles estava fora do ar.
   */
  const [propagacao, setPropagacao] = useState<string | null>(null);

  /* A lista chega do servidor por id (mais novo primeiro); na tela vale o
     padrão da suíte: nome A→Z (empate: pelo login). */
  const rowsEmOrdem = useMemo(
    () =>
      [...rows].sort(
        (a, b) => compararNomes(a.nome || a.usuario, b.nome || b.usuario) || compararNomes(a.usuario, b.usuario),
      ),
    [rows],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listUsuarios());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao carregar usuários.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function openNew() {
    setEditing(null);
    setForm(EMPTY);
    setFormError(null);
    setFormOpen(true);
  }

  function openEdit(u: Usuario) {
    setEditing(u);
    setForm({
      nome: u.nome,
      usuario: u.usuario,
      email: u.email ?? "",
      cargo: u.cargo ?? "",
      senha: "",
      ativo: u.ativo,
      admin: u.admin === true,
      sistemas: u.sistemas ?? [],
      // Cadastro da 1ª versão (caixa geral) chega como "todos os sistemas".
      sistemasLeitura: u.sistemasLeitura ?? (u.somenteLeitura ? u.sistemas ?? [] : []),
      abas: u.abas ?? {},
    });
    setFormError(null);
    setFormOpen(true);
  }

  const set = (k: keyof FormState, v: string | boolean) =>
    setForm((p) => ({ ...p, [k]: v }));

  function toggleSistema(id: string) {
    setForm((p) => {
      const tirando = p.sistemas.includes(id);
      const abas = { ...p.abas };
      if (tirando) delete abas[id];
      return {
        ...p,
        sistemas: tirando ? p.sistemas.filter((s) => s !== id) : [...p.sistemas, id],
        sistemasLeitura: p.sistemasLeitura.filter((s) => s !== id),
        abas,
      };
    });
  }

  /** Acesso normal (pode mexer) ou só visualização, sistema por sistema. */
  function setModo(id: string, soVisualizar: boolean) {
    setForm((p) => {
      const abas = { ...p.abas };
      if (!soVisualizar) delete abas[id];
      const outros = p.sistemasLeitura.filter((s) => s !== id);
      return { ...p, sistemasLeitura: soVisualizar ? [...outros, id] : outros, abas };
    });
  }

  /** Todas as abas de um sistema (chaves), na ordem do sistema. */
  const todasAbas = (sistema: string): string[] =>
    (ABAS_POR_SISTEMA[sistema as keyof typeof ABAS_POR_SISTEMA] ?? []).map((x) => x.key);

  /** Abas marcadas de um sistema (sem escolha gravada = todas). */
  const abasDe = (sistema: string): string[] => form.abas[sistema] ?? todasAbas(sistema);

  function toggleAba(sistema: string, aba: string) {
    setForm((p) => {
      const todas = todasAbas(sistema);
      const atuais = p.abas[sistema] ?? todas;
      const novas = atuais.includes(aba) ? atuais.filter((k) => k !== aba) : [...atuais, aba];
      const abas = { ...p.abas };
      // Todas marcadas = sem restrição (e aba nova do sistema já entra liberada).
      if (novas.length === todas.length && todas.every((k) => novas.includes(k))) delete abas[sistema];
      else abas[sistema] = todas.filter((k) => novas.includes(k));
      return { ...p, abas };
    });
  }

  function marcarTodasAbas(sistema: string, todas: boolean) {
    setForm((p) => {
      const abas = { ...p.abas };
      if (todas) delete abas[sistema];
      else abas[sistema] = [];
      return { ...p, abas };
    });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const semAba = form.sistemasLeitura.find((s) => form.abas[s] !== undefined && form.abas[s].length === 0);
    if (semAba) {
      const nome = SYSTEMS.find((x) => x.id === semAba)?.nome ?? semAba;
      setFormError(`Escolha ao menos uma aba em ${nome} (ou deixe o acesso normal).`);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const salvo = editing
        ? await updateUsuario(editing.id, form)
        : await createUsuario(form);
      setPropagacao(salvo?.propagacao || null);
      setFormOpen(false);
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(u: Usuario) {
    if (!confirm(`Excluir o usuário "${u.usuario}"?`)) return;
    try {
      await removeUsuario(u.id);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao excluir.");
    }
  }

  return (
    <div className="usuarios">
      <div className="content-head usuarios-head">
        <div>
          <h1>Usuários</h1>
          <p className="hello">Crie e gerencie os acessos do Portal, com cargo.</p>
        </div>
        <div className="usuarios-head-actions">
          <button className="btn btn-ghost" onClick={onBack}>
            ← Voltar
          </button>
          <button className="btn" onClick={openNew}>
            + Novo usuário
          </button>
        </div>
      </div>

      {propagacao ? (
        <div className="usuarios-propagacao">
          <strong>Acesso nos sistemas:</strong> {propagacao}.
          <button className="btn btn-ghost" onClick={() => setPropagacao(null)}>
            Fechar
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="err">{error}</div>
      ) : loading ? (
        <div className="usuarios-empty">Carregando...</div>
      ) : rows.length === 0 ? (
        <div className="usuarios-empty">Nenhum usuário cadastrado.</div>
      ) : (
        <div className="usuarios-table-wrap">
          <table className="usuarios-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Usuário</th>
                <th>E-mail</th>
                <th>Cargo</th>
                <th>Admin</th>
                <th>Sistemas</th>
                <th>Situação</th>
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {rowsEmOrdem.map((u) => (
                <tr key={u.id}>
                  <td>{u.nome}</td>
                  <td>{u.usuario}</td>
                  <td>{u.email ?? "—"}</td>
                  <td>{u.cargo ?? "—"}</td>
                  <td>
                    {u.admin && <span className="pill pill-on">Administrador</span>}{" "}
                    {(() => {
                      const leitura = u.sistemasLeitura ?? (u.somenteLeitura ? u.sistemas : []);
                      if (!leitura.length) return u.admin ? null : "—";
                      return (
                        <span className="pill pill-leitura" title="Sistemas em que a pessoa só visualiza">
                          {leitura.length === u.sistemas.length ? "Só visualização" : `Só visualiza ${leitura.length}`}
                        </span>
                      );
                    })()}
                  </td>
                  <td>{u.sistemas?.length ? `${u.sistemas.length} sistema(s)` : "—"}</td>
                  <td>
                    <span className={u.ativo === "Ativo" ? "pill pill-on" : "pill pill-off"}>
                      {u.ativo}
                    </span>
                  </td>
                  <td className="col-actions">
                    <button className="link-btn" onClick={() => openEdit(u)}>
                      Editar
                    </button>
                    <button className="link-btn link-danger" onClick={() => handleDelete(u)}>
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {formOpen && (
        <div className="modal-overlay" onMouseDown={() => setFormOpen(false)}>
          <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
            <h3>{editing ? `Editar — ${editing.usuario}` : "Novo usuário"}</h3>
            <form className="form" onSubmit={submit}>
              {formError && <div className="err">{formError}</div>}
              <label className="field">
                <span>Nome</span>
                <input value={form.nome} onChange={(e) => set("nome", e.target.value)} required />
              </label>
              <label className="field">
                <span>Usuário (login)</span>
                <input
                  value={form.usuario}
                  onChange={(e) => set("usuario", e.target.value)}
                  autoComplete="off"
                  required
                />
                <small className="field-hint">
                  É este texto que a pessoa digita na tela de entrada (o e-mail
                  abaixo também serve para entrar).
                </small>
              </label>
              <label className="field">
                <span>E-mail</span>
                <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </label>
              <label className="field">
                <span>Cargo</span>
                <input value={form.cargo} onChange={(e) => set("cargo", e.target.value)} />
              </label>
              <label className="field">
                <span>Senha</span>
                <input
                  type="password"
                  value={form.senha}
                  onChange={(e) => set("senha", e.target.value)}
                  placeholder={editing ? "Em branco mantém a senha atual" : "Defina uma senha"}
                  autoComplete="new-password"
                />
              </label>
              <label className="field">
                <span>Situação</span>
                <select value={form.ativo} onChange={(e) => set("ativo", e.target.value)}>
                  <option value="Ativo">Ativo</option>
                  <option value="Inativo">Inativo</option>
                </select>
              </label>
              <div className="field">
                <span>Permissões</span>
                <label className="sistema-check">
                  <input
                    type="checkbox"
                    checked={form.admin}
                    onChange={(e) => set("admin", e.target.checked)}
                  />
                  <span>Administrador</span>
                </label>
                <small className="field-hint">
                  Só o Administrador pode <strong>excluir</strong> registros dentro
                  dos sistemas (todo mundo continua podendo criar e editar). Nos
                  sistemas em "Só visualização" ninguém exclui nada.
                </small>
              </div>
              <div className="field">
                <span>Sistemas liberados</span>
                <div className="sistemas-grid">
                  {SISTEMAS_EM_ORDEM.map((s) => (
                    <label key={s.id} className="sistema-check">
                      <input
                        type="checkbox"
                        checked={form.sistemas.includes(s.id)}
                        onChange={() => toggleSistema(s.id)}
                      />
                      <span>{s.nome}</span>
                    </label>
                  ))}
                </div>
                {form.sistemas.length > 0 && (
                  <small className="sistemas-hint">
                    Em cada sistema marcado, escolha abaixo: <strong>Acesso normal</strong> (cria
                    e edita) ou <strong>Só visualização</strong> (só vê as abas marcadas).
                  </small>
                )}
                {SISTEMAS_EM_ORDEM.filter((s) => form.sistemas.includes(s.id)).map((s) => {
                    const todas = ABAS_POR_SISTEMA[s.id] ?? [];
                    const marcadas = abasDe(s.id);
                    const soVisualizar = form.sistemasLeitura.includes(s.id);
                    return (
                      <fieldset key={s.id} className="abas-sistema">
                        <legend>
                          {s.nome}{" "}
                          {soVisualizar && (
                            <span className="abas-conta">
                              {marcadas.length === todas.length ? "(todas as abas)" : `(${marcadas.length} de ${todas.length} abas)`}
                            </span>
                          )}
                        </legend>
                        <div className="modo-sistema" role="radiogroup" aria-label={`Acesso a ${s.nome}`}>
                          <label className="sistema-check">
                            <input
                              type="radio"
                              name={`modo-${s.id}`}
                              checked={!soVisualizar}
                              onChange={() => setModo(s.id, false)}
                            />
                            <span>Acesso normal</span>
                          </label>
                          <label className="sistema-check">
                            <input
                              type="radio"
                              name={`modo-${s.id}`}
                              checked={soVisualizar}
                              onChange={() => setModo(s.id, true)}
                            />
                            <span>Só visualização</span>
                          </label>
                        </div>
                        {soVisualizar && (<>
                        <div className="abas-atalhos">
                          <button type="button" className="link-btn" onClick={() => marcarTodasAbas(s.id, true)}>
                            Marcar todas
                          </button>
                          <button type="button" className="link-btn" onClick={() => marcarTodasAbas(s.id, false)}>
                            Limpar
                          </button>
                        </div>
                        <div className="sistemas-grid">
                          {todas.map((aba) => (
                            <label key={aba.key} className="sistema-check">
                              <input
                                type="checkbox"
                                checked={marcadas.includes(aba.key)}
                                onChange={() => toggleAba(s.id, aba.key)}
                              />
                              <span>{aba.label}</span>
                            </label>
                          ))}
                        </div>
                        </>)}
                      </fieldset>
                    );
                  })}
                <small className="sistemas-hint">
                  O Admin Supremo acessa todos os sistemas automaticamente — não
                  precisa marcar.
                </small>
              </div>
              <div className="modal-foot">
                <button type="button" className="btn btn-ghost" onClick={() => setFormOpen(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn" disabled={saving}>
                  {saving ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
